interface Solution {
  id: number;
  title: string;
  description: string;
  features?: string[];
  icon?: string;
}

interface CategorySolutions {
  [key: string]: Solution[];
}

// Expanded solutions database with more categories and solutions
export const solutionsDatabase: CategorySolutions = {
  "sales": [
    { 
      id: 1, 
      title: "AI-powered landing page with lead tracking", 
      description: "Convert more visitors into customers with smart lead tracking and personalized experiences.",
      features: ["Personalized content based on visitor behavior", "Lead scoring and qualification", "A/B testing automation"],
      icon: "trending-up"
    },
    { 
      id: 2, 
      title: "AI chatbot for sales automation", 
      description: "Engage visitors 24/7, qualify leads, and boost conversion rates automatically.",
      features: ["24/7 lead qualification", "Product recommendations", "Seamless handoff to human sales team"],
      icon: "message-square"
    },
    { 
      id: 3, 
      title: "AI-driven social media marketing tool", 
      description: "Create, schedule, and optimize campaigns with AI-driven content recommendations.",
      features: ["AI content generation", "Optimal posting time prediction", "Audience targeting optimization"],
      icon: "share-2"
    },
    { 
      id: 4, 
      title: "Predictive sales forecasting dashboard", 
      description: "Use historical data and AI to predict future sales and identify opportunities.",
      features: ["Revenue forecasting", "Pipeline analysis", "Opportunity scoring"],
      icon: "bar-chart"
    }
  ],
  "customer_support": [
    { 
      id: 1, 
      title: "AI customer service automation", 
      description: "Resolve 80% of customer queries instantly without human intervention.",
      features: ["Instant response to common questions", "Smart ticket routing", "Sentiment analysis"],
      icon: "headphones"
    },
    { 
      id: 2, 
      title: "Knowledge base with AI search", 
      description: "Help customers find answers quickly with an intelligent self-service platform.",
      features: ["Natural language search", "Content recommendation", "Usage analytics"],
      icon: "book-open"
    },
    { 
      id: 3, 
      title: "Omnichannel support platform", 
      description: "Manage all customer communications in one place with AI routing and prioritization.",
      features: ["Unified inbox for all channels", "Priority sorting", "Automated follow-ups"],
      icon: "layers"
    }
  ],
  "team_collaboration": [
    { 
      id: 1, 
      title: "AI project management system", 
      description: "Automate task assignments, deadlines, and progress tracking for your team.",
      features: ["Smart task prioritization", "Resource allocation", "Deadline prediction"],
      icon: "clipboard"
    },
    { 
      id: 2, 
      title: "Smart document collaboration platform", 
      description: "Work together on documents with AI-powered suggestions and version control.",
      features: ["Real-time collaboration", "Content suggestions", "Automated summarization"],
      icon: "file-text"
    },
    { 
      id: 3, 
      title: "Team communication hub", 
      description: "Keep everyone in sync with AI meeting summaries and action item tracking.",
      features: ["Meeting transcription and summary", "Action item extraction", "Follow-up reminders"],
      icon: "users"
    }
  ],
  "booking": [
    { 
      id: 1, 
      title: "AI appointment scheduling system", 
      description: "Let customers book appointments 24/7 with automated reminders and follow-ups.",
      features: ["24/7 online booking", "Smart scheduling to minimize gaps", "Automated reminders"],
      icon: "calendar"
    },
    { 
      id: 2, 
      title: "Resource management platform", 
      description: "Optimize staff and resource allocation with AI predictions and scheduling.",
      features: ["Staff utilization optimization", "Demand forecasting", "Automatic scheduling"],
      icon: "clock"
    },
    { 
      id: 3, 
      title: "Customer booking portal", 
      description: "Give customers a seamless self-service booking experience with payment integration.",
      features: ["Branded customer portal", "Payment processing", "Custom booking rules"],
      icon: "credit-card"
    }
  ],
  "creative": [
    { 
      id: 1, 
      title: "AI-powered portfolio platform", 
      description: "Showcase your creative work with AI-enhanced presentation and interaction.",
      features: ["Customizable portfolio layouts", "Visitor analytics", "Engagement optimization"],
      icon: "image"
    },
    { 
      id: 2, 
      title: "Creative project management tool", 
      description: "Streamline your creative process with AI-assisted workflow management.",
      features: ["Project milestone tracking", "Resource allocation", "Client collaboration portal"],
      icon: "tool"
    },
    { 
      id: 3, 
      title: "Digital asset management system", 
      description: "Organize, find, and share your creative assets with AI-powered search and tagging.",
      features: ["Automatic tagging and categorization", "Advanced search", "Usage tracking"],
      icon: "folder"
    },
    { 
      id: 4, 
      title: "AI creation assistant", 
      description: "Enhance your creative process with AI-generated ideas and variations.",
      features: ["Style suggestions", "Reference gathering", "Variation generation"],
      icon: "pen-tool"
    }
  ],
  "retail": [
    { 
      id: 1, 
      title: "Intelligent inventory management", 
      description: "Optimize stock levels and reduce waste with AI-powered demand forecasting.",
      features: ["Demand prediction", "Automatic reordering", "Seasonal adjustment"],
      icon: "package"
    },
    { 
      id: 2, 
      title: "Smart eCommerce platform", 
      description: "Boost sales with personalized product recommendations and dynamic pricing.",
      features: ["Personalized product recommendations", "Dynamic pricing", "Abandoned cart recovery"],
      icon: "shopping-cart"
    },
    { 
      id: 3, 
      title: "Customer loyalty system", 
      description: "Increase repeat business with an AI-optimized loyalty and rewards program.",
      features: ["Personalized rewards", "Customer segmentation", "Engagement tracking"],
      icon: "award"
    }
  ],
  "healthcare": [
    { 
      id: 1, 
      title: "Patient scheduling platform", 
      description: "Optimize appointment scheduling and reduce no-shows with smart reminders.",
      features: ["Online scheduling", "Automated reminders", "Wait-time predictions"],
      icon: "activity"
    },
    { 
      id: 2, 
      title: "Telehealth consultation system", 
      description: "Provide secure video consultations with integrated medical records.",
      features: ["Secure video consultations", "Electronic health records integration", "Post-visit follow-ups"],
      icon: "video"
    },
    { 
      id: 3, 
      title: "Patient engagement portal", 
      description: "Improve outcomes with personalized health education and monitoring.",
      features: ["Personalized health content", "Medication reminders", "Progress tracking"],
      icon: "heart"
    }
  ],
  "education": [
    { 
      id: 1, 
      title: "AI-powered learning platform", 
      description: "Create personalized learning paths adapted to each student's needs.",
      features: ["Adaptive learning paths", "Progress tracking", "Customized content delivery"],
      icon: "book"
    },
    { 
      id: 2, 
      title: "Virtual classroom solution", 
      description: "Deliver engaging online classes with interactive tools and analytics.",
      features: ["Interactive whiteboard", "Breakout rooms", "Engagement analytics"],
      icon: "monitor"
    },
    { 
      id: 3, 
      title: "Student information system", 
      description: "Streamline administration with an all-in-one management platform.",
      features: ["Attendance tracking", "Grade management", "Parent communication"],
      icon: "clipboard"
    }
  ],
  "consultation": [
    { 
      id: 1, 
      title: "Business process audit & automation", 
      description: "We'll analyze your workflow and identify opportunities for AI-powered automation.",
      features: ["Process mapping", "Efficiency analysis", "Automation recommendations"],
      icon: "activity"
    },
    { 
      id: 2, 
      title: "Custom AI solution consultation", 
      description: "Get personalized recommendations based on your unique business challenges.",
      features: ["Needs assessment", "Solution architecture", "ROI projection"],
      icon: "settings"
    },
    { 
      id: 3, 
      title: "AI readiness assessment", 
      description: "Understand how prepared your business is to implement AI solutions effectively.",
      features: ["Technology assessment", "Data evaluation", "Implementation roadmap"],
      icon: "check-square"
    }
  ]
};

// Message analysis functions
export const analyzeUserMessage = (message: string): string[] => {
  // Simple keyword-based classification for demo purposes
  // In a real-world scenario, this would be replaced with a proper NLP service
  const message_lower = message.toLowerCase();
  
  const categoryMatches: {[key: string]: number} = {
    sales: 0,
    customer_support: 0,
    team_collaboration: 0,
    booking: 0,
    creative: 0,
    retail: 0,
    healthcare: 0,
    education: 0,
    consultation: 0
  };
  
  // Sales keywords
  if (message_lower.includes('sale') || 
      message_lower.includes('marketing') || 
      message_lower.includes('lead') || 
      message_lower.includes('customer') || 
      message_lower.includes('sell') ||
      message_lower.includes('revenue') ||
      message_lower.includes('conversion')) {
    categoryMatches.sales += 2;
  }
  
  // Customer support keywords
  if (message_lower.includes('support') || 
      message_lower.includes('help desk') || 
      message_lower.includes('ticket') || 
      message_lower.includes('customer service') ||
      message_lower.includes('query') ||
      message_lower.includes('resolve')) {
    categoryMatches.customer_support += 2;
  }
  
  // Team collaboration keywords
  if (message_lower.includes('team') || 
      message_lower.includes('collaboration') || 
      message_lower.includes('project') || 
      message_lower.includes('document') ||
      message_lower.includes('work together') ||
      message_lower.includes('communication')) {
    categoryMatches.team_collaboration += 2;
  }
  
  // Booking keywords
  if (message_lower.includes('book') || 
      message_lower.includes('appointment') || 
      message_lower.includes('schedule') || 
      message_lower.includes('reservation') ||
      message_lower.includes('calendar') ||
      message_lower.includes('meeting')) {
    categoryMatches.booking += 2;
  }
  
  // Creative keywords
  if (message_lower.includes('artist') || 
      message_lower.includes('creative') || 
      message_lower.includes('design') || 
      message_lower.includes('portfolio') ||
      message_lower.includes('artwork') ||
      message_lower.includes('photography') ||
      message_lower.includes('creator')) {
    categoryMatches.creative += 3;  // Give higher weight to creative
  }
  
  // Retail keywords
  if (message_lower.includes('shop') || 
      message_lower.includes('store') || 
      message_lower.includes('inventory') || 
      message_lower.includes('product') ||
      message_lower.includes('retail') ||
      message_lower.includes('ecommerce')) {
    categoryMatches.retail += 2;
  }
  
  // Healthcare keywords
  if (message_lower.includes('health') || 
      message_lower.includes('medical') || 
      message_lower.includes('patient') || 
      message_lower.includes('clinic') ||
      message_lower.includes('doctor') ||
      message_lower.includes('hospital')) {
    categoryMatches.healthcare += 2;
  }
  
  // Education keywords
  if (message_lower.includes('teach') || 
      message_lower.includes('student') || 
      message_lower.includes('education') || 
      message_lower.includes('school') ||
      message_lower.includes('class') ||
      message_lower.includes('course')) {
    categoryMatches.education += 2;
  }
  
  // If user explicitly states they don't know what they need
  if (message_lower.includes('don\'t know') || 
      message_lower.includes('not sure') || 
      message_lower.includes('confused') ||
      message_lower.includes('help me decide') ||
      message_lower.includes('what do you recommend')) {
    categoryMatches.consultation += 3;
  }
  
  // Sort categories by match score
  const sortedCategories = Object.entries(categoryMatches)
    .sort((a, b) => b[1] - a[1])
    .filter(([_, score]) => score > 0)
    .map(([category, _]) => category);
  
  // If no matches, return consultation as the default
  return sortedCategories.length > 0 ? sortedCategories : ['consultation'];
};

export const getResponseForUserMessage = (message: string): {
  response: string;
  categories: string[];
  solutions: Solution[];
} => {
  const categories = analyzeUserMessage(message);
  const primaryCategory = categories[0];
  let solutions: Solution[] = [];
  
  // Get solutions for primary category
  if (primaryCategory && solutionsDatabase[primaryCategory]) {
    solutions = solutionsDatabase[primaryCategory];
  } else {
    // Fallback to consultation solutions
    solutions = solutionsDatabase.consultation;
  }
  
  // Generate personalized response based on category
  let response = '';
  switch (primaryCategory) {
    case 'sales':
      response = `Based on your message, I see you're looking to boost sales. Here are some AI-powered solutions that could help:`;
      break;
    case 'customer_support':
      response = `It sounds like you want to improve your customer support. Here are some AI-powered solutions that could help:`;
      break;
    case 'team_collaboration':
      response = `I understand you're looking to enhance team collaboration. Here are some AI-powered solutions that could help:`;
      break;
    case 'booking':
      response = `I see you're interested in improving your booking or scheduling process. Here are some AI-powered solutions that could help:`;
      break;
    case 'creative':
      response = `As an artist/creative professional, here are some AI-powered solutions that could help showcase and manage your work:`;
      break;
    case 'retail':
      response = `For your retail business, here are some AI-powered solutions that could help streamline operations and boost sales:`;
      break;
    case 'healthcare':
      response = `For your healthcare practice, here are some AI-powered solutions that could improve patient care and operations:`;
      break;
    case 'education':
      response = `For your educational needs, here are some AI-powered solutions that could enhance teaching and learning:`;
      break;
    default:
      response = `Based on your message, here are some custom AI-powered solutions that might address your specific needs:`;
  }
  
  return {
    response,
    categories,
    solutions
  };
};
